import React, { useState, useEffect, useRef } from 'react';
import { X, DollarSign, Save, MessageCircle } from 'lucide-react';

interface AddFeesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (feesData: any) => void;
  programName: string;
  className: string;
  studentName: string;
  studentId: number;
  phone: string;
  guardianName: string;
  monthYear: string;
  month: number;
  year: number;
  existingData?: {
    feeId?: number;
    totalFees?: number;
    discount?: number;
    feesReceived?: number;
    paymentMethodId?: string;
    notes?: string;
  };
}

const AddFeesModal: React.FC<AddFeesModalProps> = ({
  isOpen,
  onClose,
  onSave,
  programName,
  className,
  studentName,
  studentId,
  phone,
  guardianName,
  monthYear,
  month,
  year,
  existingData
}) => {
  const [formData, setFormData] = useState({
    totalFees: '',
    discount: '',
    feesReceived: '',
    paymentMethodId: '1', // Default to Cash
    notes: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const feesReceivedRef = useRef<HTMLInputElement>(null);

  // Payment method options
  const paymentMethodOptions = [
    { value: '1', label: 'Cash' },
    { value: '2', label: 'Bank Transfer' },
    { value: '3', label: 'Check' },
    { value: '4', label: 'Online Payment' }
  ];

  // Reset form when modal opens/closes or existingData changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        totalFees: existingData?.totalFees?.toString() || '',
        discount: existingData?.discount?.toString() || '0',
        feesReceived: existingData?.feesReceived?.toString() || '',
        paymentMethodId: existingData?.paymentMethodId?.toString() || '1', // Default to Cash (first option)
        notes: existingData?.notes || ''
      });

      console.log(formData);
      setErrors({});
      
      // Focus on fees received input after a short delay to ensure modal is fully rendered
      setTimeout(() => {
        feesReceivedRef.current?.focus();
      }, 100);
    }
  }, [isOpen, existingData]);

  // Calculate net amount (total fees - discount)
  const netAmount = () => {
    const total = parseFloat(formData.totalFees) || 0;
    const discount = parseFloat(formData.discount) || 0;
    return Math.max(0, total - discount);
  };

  // Format message for WhatsApp payment reminder
  const getWhatsAppMessage = () => {
    const netAmountValue = netAmount();
    const pendingAmount = netAmountValue - (parseFloat(formData.feesReceived) || 0);
    
    if (pendingAmount <= 0) {
      return `Salam ${guardianName},\n\nThis is a friendly reminder that your fees for student ${studentName} for ${monthYear} have been fully paid. Thank you for your prompt payment!\n\nBest regards,\nSchool Administration`;
    }
    
    return `Salam ${guardianName},\n\nThis is a friendly reminder about your pending payment for student ${studentName} for ${monthYear}.\n\nTotal Amount: $${netAmountValue.toFixed(2)}\nAmount Paid: $${(parseFloat(formData.feesReceived) || 0).toFixed(2)}\nPending Amount: $${pendingAmount.toFixed(2)}\n\nPlease arrange payment at your earliest convenience. If you have any questions, please contact us.\n\nBest regards,\nBMM School Administration`;
  };

  // Open WhatsApp with pre-filled message
  const handleWhatsAppReminder = () => {
    if (!phone) {
      alert('Student phone number is not available');
      return;
    }

    // Clean phone number (remove spaces, dashes, brackets)
    let cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    
    // Format message for URL
    const message = encodeURIComponent(getWhatsAppMessage());

   // If the phone number is 10 digits, add country code +1
   if (/^\d{10}$/.test(cleanPhone)) {
     cleanPhone = '+1' + cleanPhone;
   }
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${message}`;
    
    // Open in new tab
    window.open(whatsappUrl, '_blank');
  };

  // Get button text based on payment status
  const getWhatsAppButtonText = () => {
    const netAmountValue = netAmount();
    const pendingAmount = netAmountValue - (parseFloat(formData.feesReceived) || 0);
    
    return pendingAmount <= 0 ? 'Send Payment Confirmation' : 'Remind via WhatsApp';
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev: any) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.totalFees || parseFloat(formData.totalFees) <= 0) {
      newErrors.totalFees = 'Total fees must be greater than 0';
    }

    const discount = parseFloat(formData.discount) || 0;
    const totalFees = parseFloat(formData.totalFees) || 0;

    if (discount < 0) {
      newErrors.discount = 'Discount cannot be negative';
    }

    if (discount > totalFees) {
      newErrors.discount = 'Discount cannot be greater than total fees';
    }

    const feesReceived = parseFloat(formData.feesReceived) || 0;
    if (feesReceived < 0) {
      newErrors.feesReceived = 'Fees received cannot be negative';
    }

    if (feesReceived > netAmount()) {
      newErrors.feesReceived = 'Fees received cannot be greater than net amount';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const feesData = {
        feeId: existingData?.feeId || 0,
        studentId: studentId,
        month: month,
        year: year,
        totalFees: parseFloat(formData.totalFees),
        discount: parseFloat(formData.discount) || 0,
        feesReceived: parseFloat(formData.feesReceived) || 0,
        paymentMethodId: formData.paymentMethodId,
        notes: formData.notes.trim()
      };

      await onSave(feesData);
      onClose();
    } catch (error) {
      console.error('Error saving fees:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose}></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg dark:bg-zink-600">
          
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center size-8 bg-blue-100 rounded-md dark:bg-blue-500/20">
                <DollarSign className="size-4 text-blue-500" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-zink-50">
                {existingData?.feeId ? 'Edit Fees' : 'Add Fees'}
              </h3>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-slate-500 hover:text-slate-700 dark:text-zink-300 dark:hover:text-zink-100 disabled:opacity-50"
            >
              <X className="size-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 space-y-4">
            
            {/* Disabled Info Fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Program
                </label>
                <input
                  type="text"
                  value={programName}
                  disabled
                  className="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-slate-500 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-400"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Class
                </label>
                <input
                  type="text"
                  value={className}
                  disabled
                  className="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-slate-500 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Student Name
                </label>
                <input
                  type="text"
                  value={`${studentName} (#${studentId})`}
                  disabled
                  className="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-slate-500 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Month/Year
                </label>
                <input
                  type="text"
                  value={monthYear}
                  disabled
                  className="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-slate-500 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-400"
                />
              </div>
            </div>

            {/* Editable Fee Fields */}
            <div className="border-t border-slate-200 dark:border-zink-500 pt-4">
              <h4 className="text-sm font-medium text-slate-700 dark:text-zink-200 mb-3">Fee Details</h4>
              
              <div className="space-y-4">
                {/* Total Fees and Discount side by side */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                      Total Fees <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-slate-500 dark:text-zink-400">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.totalFees}
                        onChange={(e) => handleInputChange('totalFees', e.target.value)}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zink-700 dark:text-zink-100 ${
                          errors.totalFees 
                            ? 'border-red-300 dark:border-red-600' 
                            : 'border-slate-200 dark:border-zink-500'
                        }`}
                        placeholder="0.00"
                        disabled={isLoading}
                      />
                    </div>
                    {errors.totalFees && (
                      <p className="text-red-500 text-xs mt-1">{errors.totalFees}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                      Discount
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-slate-500 dark:text-zink-400">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.discount}
                        onChange={(e) => handleInputChange('discount', e.target.value)}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zink-700 dark:text-zink-100 ${
                          errors.discount 
                            ? 'border-red-300 dark:border-red-600' 
                            : 'border-slate-200 dark:border-zink-500'
                        }`}
                        placeholder="0.00"
                        disabled={isLoading}
                      />
                    </div>
                    {errors.discount && (
                      <p className="text-red-500 text-xs mt-1">{errors.discount}</p>
                    )}
                  </div>
                </div>

                {/* Net Amount Display */}
                {formData.totalFees && (
                  <div className="bg-slate-50 dark:bg-zink-600 p-3 rounded-md">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 dark:text-zink-200">Net Amount:</span>
                      <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                        ${netAmount().toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Fees Received and Payment Method side by side */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                      Fees Received
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-slate-500 dark:text-zink-400">$</span>
                      <input
                        ref={feesReceivedRef}
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.feesReceived}
                        onChange={(e) => handleInputChange('feesReceived', e.target.value)}
                        className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zink-700 dark:text-zink-100 ${
                          errors.feesReceived 
                            ? 'border-red-300 dark:border-red-600' 
                            : 'border-slate-200 dark:border-zink-500'
                        }`}
                        placeholder="0.00"
                        disabled={isLoading}
                      />
                    </div>
                    {errors.feesReceived && (
                      <p className="text-red-500 text-xs mt-1">{errors.feesReceived}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                      Payment Method
                    </label>
                    <select
                      value={formData.paymentMethodId}
                      onChange={(e) => handleInputChange('paymentMethodId', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-100"
                      disabled={isLoading}
                    >
                      
                      {paymentMethodOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                    Notes
                  </label>
                  <textarea
                    rows={2}
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-100"
                    placeholder="Additional notes..."
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-zink-500">
            {/* WhatsApp Button */}
            <button
              onClick={handleWhatsAppReminder}
              disabled={isLoading || !phone}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title={!phone ? "Student phone number not available" : getWhatsAppButtonText()}
            >
              <MessageCircle className="size-4 mr-2" />
              {getWhatsAppButtonText()}
            </button>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zink-700 dark:text-zink-200 dark:border-zink-500 dark:hover:bg-zink-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="size-4 mr-2" />
                    {existingData?.feeId ? 'Update Fees' : 'Save Fees'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFeesModal;
